import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BedDouble, Maximize, Eye, Check, ArrowLeft } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Lightbox from "@/components/Lightbox";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/site";

export default function RoomDetails() {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.room(id)
      .then((r) => { setRoom(r); document.title = `${r.name} | Sagar Darshan Guest House, Somnath`; })
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-sand px-5 text-center">
        <p className="font-display text-3xl text-navy">Room not found</p>
        <Link to="/rooms" data-testid="room-not-found-back" className="btn-outline-dark mt-8">Back to Rooms</Link>
      </div>
    );
  }

  if (!room) return <div className="min-h-screen bg-sand" />;

  const images = room.gallery.map((src) => ({ src, alt: `${room.name} - Sagar Darshan Guest House` }));

  return (
    <div data-testid="room-details-page" className="bg-sand pb-28 lg:pb-0">
      {/* Gallery header */}
      <section className="relative bg-navy pt-24">
        <div className="mx-auto max-w-7xl px-5 pt-10 lg:px-10">
          <Link to="/rooms" data-testid="back-to-rooms" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sand/60 transition-colors hover:text-gold">
            <ArrowLeft size={14} /> All Rooms
          </Link>
        </div>
        <div className="mx-auto grid max-w-7xl gap-2 px-5 py-8 md:grid-cols-3 lg:px-10">
          <button data-testid="room-gallery-main" onClick={() => setLightbox(0)} className="relative aspect-[4/3] overflow-hidden md:col-span-2 md:row-span-2 md:aspect-auto">
            <img src={images[0].src} alt={images[0].alt} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          </button>
          {images.slice(1, 3).map((img, i) => (
            <button key={img.src} data-testid={`room-gallery-${i + 1}`} onClick={() => setLightbox(i + 1)} className="relative hidden aspect-[4/3] overflow-hidden md:block">
              <img src={img.src} alt={img.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <span className="overline-label">{room.view}</span>
              <h1 className="mt-3 font-display text-4xl tracking-tight text-navy sm:text-5xl">{room.name}</h1>
              <p className="mt-5 max-w-2xl leading-relaxed text-navy/70">{room.description}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-9 grid grid-cols-2 gap-px border border-navy/10 bg-navy/10 sm:grid-cols-3">
                {[
                  { icon: BedDouble, label: "Bedding", value: room.bed },
                  { icon: Maximize, label: "Room Size", value: room.size },
                  { icon: Eye, label: "View", value: room.view },
                ].map((s) => (
                  <div key={s.label} className="bg-white p-5">
                    <s.icon size={20} strokeWidth={1.3} className="text-gold-dark" />
                    <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-navy/50">{s.label}</p>
                    <p className="mt-1 text-sm font-medium text-navy">{s.value}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <div className="mt-12 grid gap-12 sm:grid-cols-2">
              <Reveal delay={0.15}>
                <h2 className="font-display text-2xl text-navy">Amenities</h2>
                <ul className="mt-5 space-y-2.5">
                  {room.amenities.map((a) => (
                    <li key={a} className="flex items-center gap-2.5 text-sm text-navy/70">
                      <Check size={14} className="text-gold-dark" /> {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2}>
                <h2 className="font-display text-2xl text-navy">Included Services</h2>
                <ul className="mt-5 space-y-2.5">
                  {room.included.map((s) => (
                    <li key={s} className="flex items-center gap-2.5 text-sm text-navy/70">
                      <Check size={14} className="text-gold-dark" /> {s}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <Reveal delay={0.25}>
              <div className="mt-12 border border-navy/10 bg-white p-7">
                <h2 className="font-display text-xl text-navy">Good to Know</h2>
                <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-navy/60">
                  <li>100% advance payment is compulsory to confirm a booking.</li>
                  <li>Check-in as per your designated booking time; check-out is on a 24-hour basis.</li>
                  <li>Free cancellation till the check-in date with 100% refund to your source account. No refund for no-shows.</li>
                  <li>A valid physical photo ID (Aadhaar, Passport, Govt ID or Driving License) is mandatory at check-in.</li>
                  <li>Unmarried couples, primary guests below 18 years, and pets are not permitted.</li>
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Sticky booking card */}
          <Reveal delay={0.1}>
            <aside data-testid="room-booking-card" className="h-fit border border-navy/10 bg-white p-8 lg:sticky lg:top-28">
              <span className="text-[10px] uppercase tracking-[0.25em] text-navy/50">Starting from</span>
              <p className="mt-2 font-display text-4xl text-navy">{fmtINR(room.price)}</p>
              <p className="mt-1 text-xs text-navy/50">per night, inclusive of all taxes</p>
              <div className="my-6 h-px bg-navy/10" />
              <ul className="space-y-2 text-sm text-navy/70">
                <li className="flex justify-between"><span>Bedding</span><span>{room.bed}</span></li>
                <li className="flex justify-between"><span>View</span><span>{room.view}</span></li>
              </ul>
              <Link to={`/book?room=${room.id}`} data-testid="room-detail-book-btn" className="btn-primary mt-7 w-full">
                Book Now
              </Link>
              <p className="mt-4 text-center text-[11px] text-navy/40">Best available rate when you book direct</p>
            </aside>
          </Reveal>
        </div>
      </section>

      {/* Mobile sticky CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between border-t border-navy/10 bg-white/95 px-5 py-3 backdrop-blur-xl lg:hidden">
        <div>
          <p className="font-display text-lg text-navy">{fmtINR(room.price)} <span className="font-body text-xs text-navy/50">/ night</span></p>
        </div>
        <Link to={`/book?room=${room.id}`} data-testid="room-detail-book-btn-mobile" className="btn-primary !px-6 !py-3">Book Now</Link>
      </div>

      <Lightbox images={images} index={lightbox} onClose={() => setLightbox(null)}
        onNavigate={(d) => setLightbox((i) => (i + d + images.length) % images.length)} />
    </div>
  );
}
